import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform, Animated, Easing, ScrollView } from 'react-native';
import { BlurView } from 'expo-blur';

/**
 * AutocompleteDropdown - A hybrid text input with dropdown functionality
 * Users can type to search/filter options OR click to see the full dropdown
 */
const AutocompleteDropdown = ({ 
    label, 
    value, 
    onValueChange, 
    options = [], 
    placeholder = 'Type or select...',
    style,
    disabled = false,
    zIndex = 1,
    allowFreeText = false,
}) => {
    const [inputValue, setInputValue] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    
    // Update input value when value prop changes
    useEffect(() => {
        if (value && value !== 'all') {
            setInputValue(value);
        } else {
            setInputValue('');
        }
    }, [value]);
    
    // Filter options based on input
    useEffect(() => {
        if (inputValue.trim() === '') {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(option => 
                option.toLowerCase().includes(inputValue.toLowerCase())
            );
            setFilteredOptions(filtered);
        }
    }, [inputValue, options]);
    
    // Handle input change
    const handleInputChange = (text) => {
        setInputValue(text);
        setShowDropdown(true);
        
        if (allowFreeText) {
            // Always push the typed value up to the parent
            onValueChange(text);
        } else {
            // If exact match, select it
            const exactMatch = options.find(opt => opt.toLowerCase() === text.toLowerCase());
            if (exactMatch) {
                onValueChange(exactMatch);
            }
        }
    };
    
    // Handle option selection
    const handleSelectOption = (option) => {
        setInputValue(option);
        onValueChange(option);
        setShowDropdown(false);
    };
    
    // Handle focus
    const handleFocus = () => {
        setShowDropdown(true);
    };
    
    // Handle blur (with delay to allow click on dropdown)
    const handleBlur = () => {
        if (allowFreeText && inputValue.trim() !== '') {
            onValueChange(inputValue.trim());
        }
        setTimeout(() => {
            setShowDropdown(false);
        }, 200);
    };
    
    // Clear selection
    const handleClear = () => {
        setInputValue('');
        onValueChange('');
        setShowDropdown(false);
    };
    
    // Calculate effective z-index - when dropdown is open, boost it significantly
    const effectiveZIndex = showDropdown ? zIndex + 10000 : zIndex;
    
    return (
        <View style={[styles.container, style, { zIndex: effectiveZIndex, position: 'relative' }]}>
            {label && <Text style={styles.label}>{label}</Text>}
            
            <View style={[styles.inputContainer, { zIndex: effectiveZIndex }]}>
                <TextInput
                    ref={inputRef}
                    style={[styles.input, disabled && styles.inputDisabled]}
                    value={inputValue}
                    onChangeText={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    placeholderTextColor="rgba(255,255,255,0.4)"
                    editable={!disabled}
                />
                
                {inputValue && !disabled ? (
                    <TouchableOpacity 
                        style={styles.clearButton}
                        onPress={handleClear}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.clearButtonText}>×</Text>
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity 
                        style={styles.dropdownIcon}
                        onPress={() => !disabled && setShowDropdown(!showDropdown)}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.dropdownIconText}>{showDropdown ? '▲' : '▼'}</Text>
                    </TouchableOpacity>
                )}
            </View>
            
            {showDropdown && filteredOptions.length > 0 && (
                <View style={[styles.dropdownContainer, { zIndex: effectiveZIndex + 1 }]}>
                    <View style={styles.dropdownInner}>
                        <ScrollView 
                            style={styles.dropdownScroll}
                            nestedScrollEnabled={true}
                            showsVerticalScrollIndicator={true}
                        >
                            {filteredOptions.map((option, index) => (
                                <TouchableOpacity
                                    key={index}
                                    style={[
                                        styles.dropdownOption,
                                        option === value && styles.dropdownOptionSelected
                                    ]}
                                    onPress={() => handleSelectOption(option)}
                                    activeOpacity={0.7}
                                >
                                    <Text style={[
                                        styles.dropdownOptionText,
                                        option === value && styles.dropdownOptionTextSelected
                                    ]}>
                                        {option}
                                    </Text>
                                </TouchableOpacity>
                            ))}
                        </ScrollView>
                    </View>
                </View>
            )}
            
            {showDropdown && filteredOptions.length === 0 && inputValue.trim() !== '' && (
                <View style={[styles.dropdownContainer, { zIndex: effectiveZIndex + 1 }]}>
                    <View style={styles.dropdownInner}>
                        <View style={styles.noResultsContainer}>
                            <Text style={styles.noResultsText}>No matches found</Text>
                        </View>
                    </View>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'relative',
        width: '100%',
    },
    label: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 14,
        fontWeight: '600',
        marginBottom: 8,
    },
    inputContainer: {
        position: 'relative',
        flexDirection: 'row',
        alignItems: 'center',
    },
    input: {
        flex: 1,
        backgroundColor: '#1a1a1a',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
        borderRadius: 8,
        paddingVertical: 14,
        paddingHorizontal: 16,
        paddingRight: 40,
        color: 'rgba(255,255,255,0.95)',
        fontSize: 15,
        fontWeight: '500',
        minWidth: 200,
        ...(Platform.OS === 'web' ? {
            outlineStyle: 'none',
        } : {}),
    },
    inputDisabled: {
        opacity: 0.5,
    },
    clearButton: {
        position: 'absolute',
        right: 12,
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#3a3a3a',
        borderRadius: 12,
    },
    clearButtonText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 18,
        fontWeight: '600',
    },
    dropdownIcon: {
        position: 'absolute',
        right: 12,
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
    },
    dropdownIconText: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 12,
    },
    dropdownContainer: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        marginTop: 4,
        maxHeight: 280,
        borderRadius: 8,
        overflow: 'hidden',
        zIndex: 9999,
        ...(Platform.OS === 'web' ? {
            boxShadow: '0 12px 48px rgba(0,0,0,0.8)',
        } : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.5,
            shadowRadius: 12,
            elevation: 12,
        }),
    },
    dropdownInner: {
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.25)',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: '#0d0d0d',
    },
    dropdownScroll: {
        maxHeight: 280,
        backgroundColor: '#0d0d0d',
    },
    dropdownOption: {
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.08)',
        backgroundColor: '#0d0d0d',
    },
    dropdownOptionSelected: {
        backgroundColor: '#2d1f4e',
    },
    dropdownOptionText: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 14,
        fontWeight: '500',
    },
    dropdownOptionTextSelected: {
        color: '#e9d5ff',
        fontWeight: '600',
    },
    noResultsContainer: {
        paddingVertical: 16,
        paddingHorizontal: 16,
        alignItems: 'center',
    },
    noResultsText: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 14,
        fontStyle: 'italic',
    },
});

export default AutocompleteDropdown;

